import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, HeartPulse, Stethoscope, FlaskConical, ShoppingBag, ArrowUpRight, ChevronRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PRODUCTS } from "@/data/products";

// Real Restore Wellness clinic tour (vertical 9:16 footage).
const HERO_VIDEO = { video: "/videos/clinic-tour.mp4", poster: "/videos/clinic-tour-poster.jpg" };

const HeroVideo = ({ className, testId }) => (
  <video className={className} autoPlay muted loop playsInline preload="auto" poster={HERO_VIDEO.poster} data-testid={testId}>
    <source src={HERO_VIDEO.video} type="video/mp4" />
  </video>
);

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22,1,0.36,1] } } };

const QUICK = [
  { title: "Feel Better", desc: "Explore IV Therapy", to: "/iv-therapy", icon: HeartPulse, tint: "bg-sage-50" },
  { title: "Understand My Health", desc: "Book a Consultation", to: "/book/consult", icon: Stethoscope, tint: "bg-sand-100" },
  { title: "Stay Consistent", desc: "Explore Memberships", to: "/memberships", icon: Sparkles, tint: "bg-sage-100" },
  { title: "Support My Wellness", desc: "Shop Supplements", to: "/shop", icon: ShoppingBag, tint: "bg-sand-200/70" },
];

const SERVICES = [
  { title: "IV Therapy", desc: "Hydration and targeted nutrient support built around your goals.", to: "/iv-therapy", image: "https://restorergv.com/wp-content/uploads/2024/09/iv-therapy-2-1024x819.jpg" },
  { title: "Wellness Consultations", desc: "Meet with Steve or Lilly to design a personalized plan.", to: "/book/consult", image: "https://images.pexels.com/photos/8460095/pexels-photo-8460095.jpeg" },
  { title: "Peptide Wellness", desc: "Education and consultation on peptide-focused wellness services.", to: "/services", image: "https://restorergv.com/wp-content/uploads/2023/03/peptide-therapy-1.jpg" },
  { title: "Vitamins & Supplements", desc: "Professional-grade wellness products, curated for results.", to: "/shop", image: "https://images.unsplash.com/photo-1627467959547-8e44da7aa00a?w=900" },
  { title: "Memberships", desc: "Make IV therapy a routine — with perks that make it worth it.", to: "/memberships", image: "https://restorergv.com/wp-content/uploads/2024/11/1-1024x552.jpg" },
];

export default function Home() {
  const featured = PRODUCTS.slice(0, 4);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden grain" data-testid="hero-section">
        <div className="absolute inset-0 -z-10">
          {/* Mobile/tablet: vertical clinic tour fills the hero */}
          <HeroVideo className="lg:hidden absolute inset-0 w-full h-full object-cover" testId="hero-video-mobile" />
          <div className="lg:hidden absolute inset-0 bg-sand-50/70" />
          {/* Desktop: soft blurred poster wash behind the framed portrait video */}
          <img src={HERO_VIDEO.poster} alt="" aria-hidden="true" className="hidden lg:block absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-sand-50/95 via-sand-50/70 to-sand-50/30" />
          {/* Subtle bottom warmth */}
          <div className="absolute inset-0 bg-gradient-to-t from-sand-100/85 via-transparent to-transparent" />
          {/* Warmth over the top-right */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(230,201,127,0.22),transparent_55%)]" />
        </div>
        <div className="container-x pt-24 pb-28 lg:pt-24 lg:pb-24 lg:grid lg:grid-cols-[1fr_auto] lg:items-center lg:gap-16">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="max-w-3xl">
            <div className="overline text-sage-700 mb-5">Personalized wellness · McAllen, TX</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-sage-800 text-balance">
              Feel Better. <span className="italic text-clay-600">Live Better.</span> Restore You.
            </h1>
            <p className="mt-6 text-lg text-sage-800/70 max-w-xl leading-relaxed">
              Personalized wellness designed to help you feel your best — from IV therapy and consultations to targeted supplements and ongoing care.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Button asChild className="btn-pill h-12 px-7 bg-sage-700 hover:bg-sage-800 text-white shadow-none" data-testid="hero-book-consult">
                <Link to="/book/consult">Book a Consult</Link>
              </Button>
              <Button asChild variant="outline" className="btn-pill h-12 px-7 border-sage-700/30 text-sage-800 bg-white/60 hover:bg-white" data-testid="hero-explore-services">
                <Link to="/services">Explore Services</Link>
              </Button>
              <Link to="/login" className="ml-1 text-sm text-sage-800/70 hover:text-sage-800 inline-flex items-center gap-1" data-testid="hero-member-link">
                Already a member? Book your IV <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }} className="hidden lg:block relative">
            <div className="absolute -inset-3 rounded-[2.4rem] border border-clay-500/40" aria-hidden="true" />
            <HeroVideo className="relative w-[300px] xl:w-[340px] aspect-[9/16] object-cover rounded-[2rem] shadow-[0_30px_80px_rgba(79,60,28,0.28)]" testId="hero-video" />
            <div className="absolute bottom-5 left-5 rounded-full bg-white/85 backdrop-blur px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] text-sage-800" data-testid="hero-video-label">Tour our clinic</div>
          </motion.div>
        </div>
      </section>

      {/* QUICK ACTION */}
      <section className="section-y" data-testid="quick-actions">
        <div className="container-x">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="overline">I want to…</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-sage-800 mt-3">Where would you like to start?</h2>
            </div>
            <Link to="/quiz" className="text-sm text-sage-700 hover:underline" data-testid="quick-take-quiz">Take our 60-second quiz →</Link>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {QUICK.map((q, i) => (
              <motion.div key={q.title} initial={{opacity:0,y:16}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6, delay:i*0.08}}>
                <Link to={q.to} data-testid={`quick-${q.title.toLowerCase().replace(/\s+/g,'-')}`} className={`card-lift block ${q.tint} rounded-3xl p-7 border border-sand-200/50 h-full`}>
                  <q.icon className="w-8 h-8 text-sage-700" strokeWidth={1.4} />
                  <div className="mt-8">
                    <div className="font-display text-2xl text-sage-800">{q.title}</div>
                    <div className="mt-1.5 text-sage-800/60 text-sm flex items-center gap-1">{q.desc} <ChevronRight className="w-4 h-4" /></div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES bento */}
      <section className="section-y bg-sand-100/50" data-testid="services-preview">
        <div className="container-x">
          <div className="max-w-2xl mb-14">
            <div className="overline">Services</div>
            <h2 className="font-display text-4xl lg:text-5xl text-sage-800 mt-3">A wellness ecosystem built around you.</h2>
          </div>
          <div className="grid gap-5 lg:grid-cols-6 lg:grid-rows-2">
            {SERVICES.map((s, i) => {
              const spans = [
                "lg:col-span-3 lg:row-span-2",
                "lg:col-span-3",
                "lg:col-span-2",
                "lg:col-span-2",
                "lg:col-span-2",
              ];
              return (
                <Link key={s.title} to={s.to} data-testid={`service-${i}`} className={`card-lift group relative overflow-hidden rounded-3xl bg-white border border-sand-200/60 ${spans[i]}`}>
                  <div className="relative aspect-[4/3] lg:aspect-auto lg:h-full overflow-hidden">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-sage-800/70 via-sage-800/10 to-transparent" />
                    <div className="absolute inset-0 p-6 sm:p-8 flex flex-col justify-end text-white">
                      <div className="font-display text-2xl sm:text-3xl">{s.title}</div>
                      <div className="text-sand-100/85 text-sm mt-2 max-w-sm">{s.desc}</div>
                      <div className="mt-4 inline-flex items-center gap-1 text-sm">Learn more <ArrowUpRight className="w-4 h-4" /></div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* QUIZ CTA */}
      <section className="section-y" data-testid="quiz-cta-section">
        <div className="container-x">
          <div className="relative overflow-hidden rounded-3xl bg-sage-700 text-white p-10 sm:p-16 grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <div className="overline text-sand-100/80">Not sure where to start?</div>
              <h3 className="font-display text-3xl sm:text-4xl mt-3">Answer a few quick questions — we'll help point you in the right direction.</h3>
              <p className="mt-4 text-sand-100/80 max-w-md">Takes about 60 seconds. Get a personalized recommendation for your next step.</p>
              <Button asChild className="btn-pill h-12 px-7 mt-7 bg-white text-sage-800 hover:bg-sand-100" data-testid="quiz-cta-btn">
                <Link to="/quiz">Take the wellness quiz</Link>
              </Button>
            </div>
            <div className="md:col-span-2">
              <div className="grid grid-cols-2 gap-3">
                {["Energy","Recovery","Hydration","Healthy aging","Immunity","General wellness"].map((t)=> (
                  <div key={t} className="rounded-2xl bg-white/10 border border-white/15 px-4 py-3 text-sm">{t}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROVIDERS */}
      <section className="section-y bg-sand-100/60" data-testid="providers-section">
        <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline">Meet the team</div>
            <h2 className="font-display text-4xl lg:text-5xl text-sage-800 mt-3">Care that begins with a conversation.</h2>
            <p className="mt-5 text-sage-800/70 max-w-lg leading-relaxed">
              Book a consultation with Steve or Lilly to discuss your goals, review your current routine, and design a plan that actually fits your life.
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <Button asChild className="btn-pill h-11 px-6 bg-sage-700 hover:bg-sage-800 text-white" data-testid="providers-book-steve">
                <Link to="/book/consult?provider=steve">Book with Steve</Link>
              </Button>
              <Button asChild variant="outline" className="btn-pill h-11 px-6 border-sage-700/30 text-sage-800" data-testid="providers-book-lilly">
                <Link to="/book/consult?provider=lilly">Book with Lilly</Link>
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-3xl overflow-hidden aspect-[3/4] card-lift bg-white">
              <img src="https://restorergv.com/wp-content/uploads/2024/11/stephenmartinez.jpg" alt="Steve" className="w-full h-full object-cover" />
            </div>
            <div className="rounded-3xl overflow-hidden aspect-[3/4] card-lift bg-white mt-10">
              <img src="https://restorergv.com/wp-content/uploads/2023/07/lily-partida.jpg" alt="Lilly" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* SHOP TEASE */}
      <section className="section-y" data-testid="home-shop">
        <div className="container-x">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="overline">The Shop</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-sage-800 mt-3">Wellness doesn't stop when you leave.</h2>
            </div>
            <Link to="/shop" className="text-sm text-sage-700 hover:underline" data-testid="home-shop-all">Shop all wellness →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((p) => (
              <Link key={p.id} to={`/shop/${p.id}`} data-testid={`home-product-${p.id}`} className="card-lift block rounded-3xl bg-white border border-sand-200/60 overflow-hidden">
                <div className="aspect-square overflow-hidden bg-sand-100">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                </div>
                <div className="p-5">
                  <div className="text-xs uppercase tracking-widest text-clay-600">{p.category}</div>
                  <div className="font-display text-lg text-sage-800 mt-1">{p.name}</div>
                  <div className="mt-2 text-sage-800/70">${p.price}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-y bg-sage-800 text-white" data-testid="testimonials">
        <div className="container-x">
          <div className="overline text-clay-400">Testimonials</div>
          <h2 className="font-display text-4xl lg:text-5xl mt-3 max-w-2xl">The Restore experience, in their words.</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { name:"J. Olivares", text:"In just a few weeks, Steve literally 'restored' my energy with natural products, along with fixing gut issues I didn't even know I had." },
              { name:"D. Bailey", text:"Restore Wellness has been life changing for me!! Their knowledge and professionalism make my journey a healthy victory." },
              { name:"T. Paul", text:"Hands down the best choice I've ever made for my own health. This journey has just begun." },
            ].map((t) => (
              <div key={t.name} className="rounded-3xl bg-white/5 border border-white/10 p-7 backdrop-blur">
                <div className="flex gap-1 text-clay-400">{[...Array(5)].map((_,i)=><Star key={i} className="w-4 h-4 fill-clay-400" />)}</div>
                <p className="mt-4 text-sand-100/85 leading-relaxed">"{t.text}"</p>
                <div className="mt-6 text-sm text-sand-100/60">— {t.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-y" data-testid="final-cta">
        <div className="container-x text-center">
          <div className="overline">Ready when you are</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-sage-800 mt-4 max-w-3xl mx-auto text-balance">Take the first step toward a healthier, happier you.</h2>
          <div className="mt-9 flex justify-center gap-3 flex-wrap">
            <Button asChild className="btn-pill h-12 px-7 bg-sage-700 hover:bg-sage-800 text-white" data-testid="final-book-consult">
              <Link to="/book/consult">Book a Consult</Link>
            </Button>
            <Button asChild variant="outline" className="btn-pill h-12 px-7 border-sage-700/30 text-sage-800" data-testid="final-join-member">
              <Link to="/memberships">Join Restore</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
